import { Logger } from "@suse-o11y/logger";
import { useThrottle } from "@suse-o11y/use-throttle";
import { useEffect } from "react";

const logger = Logger.namespace("useScrollPagination");

interface UseScrollPaginationParams {
	scrollContainer: HTMLElement | null;
	threshold: number;
	fetchNextPage: () => Promise<unknown>;
	resetQuery: VoidFunction;

	/**
	 * @default true
	 */
	enabled?: boolean;

	/**
	 * Throttle time in ms for scroll event
	 *
	 * @default 100
	 */
	scrollThrottle?: number;
}

export function useScrollPagination({
	scrollContainer,
	threshold,
	fetchNextPage,
	resetQuery,
	enabled = true,
	scrollThrottle = 100,
}: UseScrollPaginationParams): void {
	// Called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
	const fetchMoreOnBottomReached = useThrottle(() => {
		// once the user has scrolled over threshold, looking from the bottom of the container, fetch more data if enabled
		if (
			enabled &&
			scrollContainer != null &&
			scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < threshold
		) {
			fetchNextPage().catch((error) => logger.error(error));
		}
	}, scrollThrottle);

	/**
	 * fetch more data as soon as enabled
	 */
	useEffect(() => {
		if (enabled) {
			fetchMoreOnBottomReached();
		}
	}, [fetchMoreOnBottomReached, enabled]);

	/**
	 * attach the scroll listener once per lifecycle
	 */
	useEffect(() => {
		if (scrollContainer != null) {
			scrollContainer.addEventListener("scroll", fetchMoreOnBottomReached);

			return () => {
				scrollContainer.removeEventListener("scroll", fetchMoreOnBottomReached);
			};
		}
	}, [fetchMoreOnBottomReached, scrollContainer]);

	// An infinite query only has one cache entry, with multiple pages. We want to avoid such behaviour, on order or filter change we want to fetch only the first page, not all currently visible pages
	// https://tanstack.com/query/latest/docs/react/guides/infinite-queries?from=reactQueryV3&original=https%3A%2F%2Freact-query-v3.tanstack.com%2Fguides%2Finfinite-queries#what-happens-when-an-infinite-query-needs-to-be-refetched
	useEffect(
		() => () => {
			resetQuery();
		},
		[resetQuery],
	);
}
